/**
 * Main orchestrator hook that combines navigation functionality
 *
 * What it does:
 * - Combines three smaller hooks to handle navigation completely
 * - Fetches navigation data from API
 * - Loads templates for the navigation steps
 * - Automatically sets up the first step
 * - Returns everything other components need
 *
 * Why it's split this way:
 * - Each hook has one clear job (single responsibility)
 * - Easier to test each piece separately
 * - Easier to modify one part without affecting others
 * - Cleaner code that's easier to understand
 *
 * How it works:
 * 1. useNavigationState: Gets navigation steps from API
 * 2. useTemplateLoader: Loads templates for those steps
 * 3. useStepInitializer: Sets up the first step automatically
 */
import type { NavigationData } from '../data-layer/reduxTypes';

import useNavigationState from './useNavigationState';
import useStepInitializer from './useStepInitializer';
import useTemplateLoader from './useTemplateLoader';

interface UseNavigationDataReturn {
  navigation: NavigationData;
  isLoading: boolean;
  isError: boolean;
}

const useNavigationData = (): UseNavigationDataReturn => {
  // Get navigation data and loading states
  const { navigation, isLoading, isError } = useNavigationState();
  const { leftNavLabels, mainCategories } = navigation;
  // Load templates for the navigation categories

  useTemplateLoader(mainCategories);
  // Set up the first step automatically
  useStepInitializer(leftNavLabels);
  // Return everything the component needs

  return { navigation, isLoading, isError };
};

export default useNavigationData;
